'use client'

import { useEffect, useRef, useState } from 'react'
import { MessageSquare, Command, Flame, Palette } from 'lucide-react'

const TABS = [
  {
    id: 'askalgo',
    icon: MessageSquare,
    title: 'Ask Algo',
    description:
      'Ask the AI analyst anything about your live chart. It reads active indicators, draws levels, and delivers a verdict with full reasoning — in seconds.',
    video: '/previews/askalgo.mov',
  },
  {
    id: 'cmdk',
    icon: Command,
    title: 'Command Menu',
    description:
      'Every action, pair, and setting reachable from a single keystroke. Trade, analyse, or navigate without ever leaving the keyboard.',
    video: '/previews/cmdk.mov',
  },
  {
    id: 'liquidation',
    icon: Flame,
    title: 'Liquidation Map',
    description:
      'See exactly where leveraged positions are stacked across the market. Spot the cascade before it happens and position ahead of the move.',
    video: '/previews/liquidation.mov',
  },
  {
    id: 'themes',
    icon: Palette,
    title: 'Themes',
    description:
      'Customise your workspace with a full palette of themes. Deep dark, high-contrast, or anything in between — your edge, your aesthetic.',
    video: '/previews/themes.mov',
  },
]

function VideoFrame({ src, active }: { src: string; active: boolean }) {
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const v = videoRef.current
    if (!v) return
    if (active) {
      v.currentTime = 0
      v.play().catch(() => {})
    } else {
      v.pause()
    }
  }, [active])

  return (
    <video
      ref={videoRef}
      src={src}
      muted
      loop
      playsInline
      className="w-full rounded object-cover"
    />
  )
}

export function FeatureTabsSection() {
  const [selected, setSelected] = useState(0)

  return (
    <section
      className="bg-black py-16 md:py-32"
      style={{ fontFamily: 'var(--font-ppmori, PPMori), sans-serif' }}
    >
      <div className="mx-auto max-w-5xl px-6">
        {/* Header */}
        <div className="mb-12 max-w-2xl">
          <h2 className="text-4xl font-medium text-white lg:text-5xl">
            Built for how traders actually work.
          </h2>
          <p className="mt-4 text-lg text-white/50">
            Every feature is designed around speed and clarity — so the time between insight and execution is as short as possible.
          </p>
        </div>

        {/* Underline tabs */}
        <div className="mb-8 flex gap-6 border-b border-white/10 overflow-x-auto scrollbar-none">
          {TABS.map((tab, i) => {
            const Icon = tab.icon
            const isActive = selected === i
            return (
              <button
                key={tab.id}
                onClick={() => setSelected(i)}
                className="relative flex shrink-0 items-center gap-2 pb-3 text-sm transition-colors"
                style={{ color: isActive ? '#fff' : 'rgba(255,255,255,0.35)' }}
              >
                <Icon className="size-3.5 shrink-0" />
                {tab.title}
                {isActive && (
                  <span className="absolute bottom-0 left-0 right-0 h-px bg-[#ffb703]" />
                )}
              </button>
            )
          })}
        </div>

        {/* Description */}
        <p className="mb-6 text-sm text-white/50 max-w-xl">
          {TABS[selected].description}
        </p>

        {/* Yosemite background container */}
        <div
          className="relative overflow-hidden"
          style={{
            backgroundImage: `url(/previews/yosemite.webp)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="p-6">
            {/* Video cards — render all, only show active */}
            <div className="relative">
              {TABS.map((tab, i) => (
                <div
                  key={tab.id}
                  className="transition-opacity duration-300"
                  style={{
                    position: i === 0 ? 'relative' : 'absolute',
                    inset: 0,
                    opacity: selected === i ? 1 : 0,
                    pointerEvents: selected === i ? 'auto' : 'none',
                  }}
                >
                  <VideoFrame src={tab.video} active={selected === i} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
